import { z } from 'zod';

// Enhanced timestamp schema to handle Unix timestamps (numbers) as well
const TimestampSchema = z.union([
  z.date(),
  z.object({
    seconds: z.number(),
    nanoseconds: z.number()
  }),
  z.string().datetime().optional(),
  z.number(), // Add support for Unix timestamps
  z.null()
]);

export const IssueSchema = z.object({
  // Add the id property that's used in the table
  id: z.string().optional(),
  
  Issue: z.object({
    ID: z.string(),
    definition: z.string()
  }),
  
  // Enhanced Installation schema with all properties used in getCombinedKey
  Installation: z.object({
    Dev_type: z.string(),
    Center: z.string().optional(),
    ID: z.string().optional(),
    Organization: z.string().optional(),
    Unit_ID: z.string().optional()
  }),

  // Set by Osiris when the issue is created
  createdAt: TimestampSchema.optional(),

  // Populated by the webapp itself (close history, comments) - Osiris no
  // longer writes anything under this key, so the whole object is optional
  timestamps: z.object({
    closed: z.object({
      Created_at: TimestampSchema
    }).optional(),
    inProgress: z.object({
      Created_at: TimestampSchema
    }).optional()
  }).passthrough().optional()
});

// Enhanced validation with detailed debugging
export const validateIssue = (issue) => {
  try {
    IssueSchema.parse(issue);
    return true;
  } catch (error) {
    // Detailed error logging for debugging
    console.group(`❌ Issue validation failed for issue ID: ${issue?.id || 'unknown'}`);
    console.log('Full issue object:', issue);
    console.log('Validation errors:', error.issues || error.errors);
    
    // Check each major section
    if (!issue?.Issue) {
      console.log('❌ Missing Issue object');
    } else {
      console.log('✅ Issue object exists:', issue.Issue);
      
      // Check Issue properties
      if (!issue.Issue.ID) console.log('❌ Missing Issue.ID');
      if (!issue.Issue.definition) console.log('❌ Missing Issue.definition');
    }
    
    console.groupEnd();
    return false;
  }
};

// Helper function to handle different timestamp formats including Unix timestamps
export const formatIssueTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  if (typeof timestamp === 'number') {
    return new Date(timestamp); // Convert from seconds to milliseconds
  }
  
  // Handle Firebase timestamp format
  if (typeof timestamp === 'object' && timestamp.seconds) {
    const milliseconds = timestamp.seconds * 1000 + Math.floor(timestamp.nanoseconds / 1e6);
    return new Date(milliseconds);
  }
  
  // Handle Date object
  if (timestamp instanceof Date) {
    return timestamp;
  }
  
  // Handle string dates
  if (typeof timestamp === 'string') {
    return new Date(timestamp);
  }
  
  return null;
};

export const getIssueProperty = (issue, propertyPath) => {
  if (!issue || !propertyPath) return null;
  
  const paths = propertyPath.split('.');
  let current = issue;
  
  for (const path of paths) {
    if (current && typeof current === 'object' && path in current) {
      current = current[path];
    } else {
      return null;
    }
  }
  
  return current;
};

// Enhanced validation with detailed error reporting
export const validateIssueWithDetails = (issue) => {
  try {
    const validated = IssueSchema.parse(issue);
    return { success: true, data: validated, errors: null };
  } catch (error) {
    return { 
      success: false, 
      data: null, 
      errors: error.issues || error.errors || [{ message: error.message }]
    };
  }
};

// Helper to get combined key as used in the component
export const getIssueCombinedKey = (issue) => {
  if (!validateIssue(issue)) return null;
  
  const installation = issue.Installation;
  const issueData = issue.Issue;
  
  const installationKey = `${installation.Center}-${installation.Dev_type}-${installation.ID}-${installation.Organization}-${installation.Unit_ID}`;
  const issueKey = `${issueData.ID}-${issueData.ID.slice(2)}`;
  return `${installationKey}-${issueKey}`;
};

export type Issue = z.infer<typeof IssueSchema>;
